import { LanguageNotAvailable } from '../Shared/DefaultPageContainer/LanguageNotAvailable';
import { Components, useEpiserver, useContentDeliveryAPI } from '@episerver/spa-core';
import { ResponsiveImageUrl } from '../Shared/ResponsiveImage/ResponsiveImageUrl';
import { NewsPageProps } from '../../Models/Content/NewsPageData';
import { Typography } from '@norges-domstoler/dds-components';
import { useEffect } from 'react';
import { dateTimeFormat } from '../Shared/Utils';
import { adjustLinks } from '../../utils/adjustLinks';
import { Heading } from '../Shared/Heading/Heading';
import { getDescription } from '../Shared/Utils';
import Breadcrumb from '../Shared/BreadCrumbAndBanner/Breadcrumb';
import { PopOver } from '../../utils/PopOver';
import '../../styles/Page/NewsPage.scss';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useNavigate } from 'react-router';
import { LanguageTag } from 'app/Models/Constants/LanguageTypes';

export const NewsPage = ({ data }: NewsPageProps) => {
    const context = useEpiserver();
    const api = useContentDeliveryAPI();
    const navigate = useNavigate();

    useEffect(() => {
        adjustLinks({ navigate });
    });

    useEffect(() => {
        let isCancelled: boolean = false;

        PopOver(api, false);

        return () => {
            isCancelled = true;
        };
    }, [data?.language?.name, data?.contentLink?.id]);

    const title: string = data?.heading?.value || (typeof data?.name === 'string' ? data.name : data?.name?.value);
    const description = getDescription(data);
    const currentUrl = data.url || (typeof window !== 'undefined' ? window.location.href : '');
    const currentLanguage = data.language?.name ?? LanguageTag.NORWEGIAN;

    const metaTags = (
        <>
            <title>{title}</title>
            <meta property="og:url" content={currentUrl} />
            <meta name="pageid" content={data.contentLink?.id?.toString() || ''} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta name="description" content={description} />

            <meta property="og:type" content="article" />
            <meta property="og:locale" content={currentLanguage} />

            {data.mainImage?.value?.url && <meta property="og:image" content={data.mainImage.value.url} />}
        </>
    );

    return (
        <>
            <Breadcrumb isNewsPage={true} currentContent={data} />
            <div className="newspage-container page-type-margin-bottom breadcrumb-margin blue">
                {context.isServerSideRendering() ? (
                    <Helmet htmlAttributes={{ lang: currentLanguage }}>{metaTags}</Helmet>
                ) : (
                    metaTags
                )}
                <div className="language-not-avaiable-newspage">
                    <div className="container page-container">
                        <LanguageNotAvailable iContent={data} />
                    </div>
                </div>
                <div className="newspage-blue">
                    <div className="container page-container">
                        <div className="row me-0 pe-0 me-md-5 pe-md-5">
                            <div className="col-12 col-lg-9">
                                <Heading {...data} />
                            </div>
                            <Typography typographyType="bodyXsmall" className="col-12 col-lg-6 pb-1 pt-3 sub-menu">
                                {dateTimeFormat(data.startPublish, data.language.name ?? '')}
                                {data.writerName.value && ', ' + data.writerName.value}
                            </Typography>
                        </div>
                    </div>
                </div>

                {data.mainImage && data.mainImage.value && (
                    <div className="newspage-image pt-3">
                        <div className="container page-container">
                            <div className="row me-0 pe-0 me-md-5 pe-md-5 news-image-container-row">
                                <div className="col-12 col-lg-6 news-image-container">
                                    <ResponsiveImageUrl
                                        alt={data.mainImageAltText?.value ?? data.mainImageCaption?.value}
                                        caption={data.mainImageCaption?.value ? data.mainImageCaption.value : null}
                                        url={data?.mainImage?.value?.url}
                                        aspectRatio={'16/9'}
                                    ></ResponsiveImageUrl>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="container page-container news-page-list-container">
                    <div className="row me-0 pe-0 me-md-5 pe-md-5">
                        <div className="col-12 col-lg-6">
                            {((data.mainIntro && data.mainIntro.value !== '') || context.isInEditMode()) && (
                                <div id="ingress">
                                    <Typography className="ingress-lead" typographyType="leadMedium" as="div">
                                        <Components.Property
                                            className="xhtml-container"
                                            iContent={data}
                                            field="mainIntro"
                                        />
                                    </Typography>
                                </div>
                            )}

                            {((data.mainBody && data.mainBody.value !== '') || context.isInEditMode()) && (
                                <div className="pt-3 newscontent" id="main-body">
                                    <Typography typographyType="bodyMedium" as="div">
                                        <Components.Property
                                            className="xhtml-container"
                                            iContent={data}
                                            field="mainBody"
                                        />
                                    </Typography>
                                </div>
                            )}

                            {data.mainContent && data.mainContent.value != null && (
                                <Components.ContentArea data={data.mainContent} propertyName="mainContent" />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
export default NewsPage;
